import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { reportsApi } from '@/api/endpoints/reports';
import { useFleetStore } from '@/stores/fleet';
import { describeEvent } from '@/lib/traccarLabels';
import { eventRisk, RISK_META, type Risk } from '@/lib/eventRisk';
import { cn, formatDateTime } from '@/lib/utils';
import {
  exportCsv,
  ReportFilters,
  ReportShell,
  ReportTable,
  ResultBar,
  useReportFilters,
} from './_shared';
import type { Event } from '@/api/types';

export function EventsReportPage() {
  const devices = useFleetStore((s) => s.devices);
  const { deviceId, setDeviceId, preset, setPreset, executed, generate } = useReportFilters('multi');
  const [search, setSearch] = useState('');
  const [riskFilter, setRiskFilter] = useState<'all' | Risk>('all');

  const query = useQuery({
    queryKey: ['report', 'events', executed],
    queryFn: () =>
      reportsApi.events({
        deviceId: executed!.ids,
        from: executed!.range.from,
        to: executed!.range.to,
      }),
    enabled: executed !== null,
  });

  const filtered = useMemo<Event[]>(() => {
    const list = query.data ?? [];
    const q = search.trim().toLowerCase();
    let result = q
      ? list.filter((e) => describeEvent(e).toLowerCase().includes(q))
      : list;
    if (riskFilter !== 'all') result = result.filter((e) => eventRisk(e.type) === riskFilter);
    return [...result].sort(
      (a, b) => new Date(b.serverTime).getTime() - new Date(a.serverTime).getTime(),
    );
  }, [query.data, search, riskFilter]);

  const handleExport = () => {
    if (!executed) return;
    exportCsv(
      `eventos_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      ['Data/Hora', 'Embarcação', 'ID Único', 'Risco', 'Tipo do evento', 'Tipo (cru)'],
      filtered.map((e) => {
        const device = e.deviceId != null ? devices.get(e.deviceId) : undefined;
        return [
          formatDateTime(e.serverTime),
          device?.name ?? '',
          device?.uniqueId ?? '',
          RISK_META[eventRisk(e.type)].label,
          describeEvent(e),
          e.type,
        ];
      }),
    );
  };

  return (
    <ReportShell title="Eventos" description="Lista de eventos do servidor por embarcação e período.">
      <ReportFilters
        deviceMode="multi"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        onGenerate={generate}
        generating={query.isFetching}
        extra={
          <>
            <label className="space-y-1">
              <span className="text-xs font-semibold text-muted-foreground">Buscar tipo</span>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ex: operação, geocerca"
                  className="pl-9"
                />
              </div>
            </label>
            <label className="space-y-1">
              <span className="text-xs font-semibold text-muted-foreground">Nível de risco</span>
              <select
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value as 'all' | Risk)}
                className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="all">Todos os riscos</option>
                <option value="critico">Crítico</option>
                <option value="atencao">Atenção</option>
                <option value="normal">Normal</option>
              </select>
            </label>
          </>
        }
      />
      {executed && (
        <ResultBar
          loading={query.isLoading}
          count={filtered.length}
          unit="eventos"
          onExport={handleExport}
        />
      )}
      <ReportTable
        notRun={executed === null}
        loading={query.isLoading}
        error={query.error}
        empty={filtered.length === 0}
      >
        {/* rolagem horizontal: tabela larga nao pode empurrar a pagina inteira no celular */}
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-semibold">Data/Hora</th>
              <th className="px-4 py-2 font-semibold">Embarcação</th>
              <th className="px-4 py-2 font-semibold">Risco</th>
              <th className="px-4 py-2 font-semibold">Tipo do evento</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 1000).map((e, i) => {
              const device = e.deviceId != null ? devices.get(e.deviceId) : undefined;
              return (
                <tr
                  key={`${e.id}-${i}`}
                  className="border-b border-border last:border-0 hover:bg-accent/40"
                >
                  <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                    {formatDateTime(e.serverTime)}
                  </td>
                  <td className="px-4 py-2">
                    {device ? (
                      <Link
                        to={`/devices/${device.id}`}
                        className="font-semibold text-primary hover:underline"
                      >
                        {device.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', RISK_META[eventRisk(e.type)].badge)}>
                      {RISK_META[eventRisk(e.type)].label}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-medium">{describeEvent(e)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {filtered.length > 1000 && (
          <p className="border-t border-border p-3 text-center text-xs text-muted-foreground">
            Exibindo primeiros 1000 eventos. Use o filtro pra reduzir.
          </p>
        )}
      </ReportTable>
    </ReportShell>
  );
}
